import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { Button, Tab, Tabs, Typography } from "@mui/material";
import DisplayUserInformation from "./DisplayUserInformation";
import RecentTransactionList from "components/common/transactions/RecentTransactionList";
import UserInformation from "./UserInformation";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import IdNotFoundPage from "../../common/IdNotFoundPage";
import useUserProfileData from "hooks/useUserProfileData";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import AssignedCards from "./AssignedCards";
import RenderUserStatus from "../../common/RenderUserStatus";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import { fullName } from "utils/fullName";
import ProfilePictureMenu from "./ProfilePictureMenu";

interface UserProfileDrawerProps {
  selectedUserId?: string;
  open: boolean;
  edit?: boolean;
  isReactivateUser?: boolean;
  handleCloseDrawer: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
  handleUpdateUser?: (error?: string) => void;
}

const UserProfileDrawer: React.FC<UserProfileDrawerProps> = (props) => {
  const {
    selectedUserId,
    open,
    edit = false,
    isReactivateUser = false,
    handleCloseDrawer,
    onAfterClose,
    handleUpdateUser,
  } = props;

  const { t } = useTranslation();
  const { user: loggedInUser } = useGetLoggedInUser();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const ownRecord = !!selectedUserId && selectedUserId === loggedInUser?.id;
  // Admins edit their own record as they edit anyone's; everyone else edits only their own, through updateMyProfile.
  const self = ownRecord && !isAdminOrHigher;

  const [isEditable, setIsEditable] = useState<boolean>(edit);
  const [reactivateUser, setReactivateUser] =
    useState<boolean>(isReactivateUser);

  useEffect(() => {
    setReactivateUser(isReactivateUser);
  }, [isReactivateUser]);

  const {
    formikAddUser,
    customFieldSettings,
    cards,
    specificLoadingStates,
    refetchCardData,
  } = useUserProfileData({
    // Until the viewer loads, your own record reads as someone else's and would ask userById, which is Manager and above.
    selectedUserId: loggedInUser ? selectedUserId : undefined,
    loadCards: true,
    reactivateUserAfterEdit: Boolean(reactivateUser),
    handleUpdateUser,
    setIsEditable,
    setReactivateUser,
    self,
  });

  const isLoadingUsers =
    (!loggedInUser || specificLoadingStates.loadingUsers) &&
    !formikAddUser.values.id;

  const { validateAndTouch } = useFormikHelpers(formikAddUser);

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    formikAddUser.dirty,
    handleCloseDrawer,
  );

  // The mode resets once the drawer has slid out, so the form does not turn into the summary while it closes.
  const handleAfterClose = () => {
    setIsEditable(edit);
    onAfterClose?.();
  };

  const cardDrawer = useDrawerRecord<true>();

  const [activeTab, setActiveTab] = useState<"transactions" | "cards">("cards");

  const handleCreateNewCard = () => {
    cardDrawer.show(true);
  };

  const handleCloseCreateCardDrawer = (isReloadData: boolean = false) => {
    if (isReloadData) {
      refetchCardData();
      handleUpdateUser && handleUpdateUser();
    }
    cardDrawer.close();
  };

  useEffect(() => {
    setIsEditable(edit);
  }, [edit]);

  const busy =
    specificLoadingStates.isUpdateUser ||
    specificLoadingStates.reactivatingUser;

  const userFullName = fullName(formikAddUser.values);

  return (
    <>
      <Drawer
        open={open}
        onClose={requestLeave}
        onAfterClose={handleAfterClose}
        title={
          isEditable
            ? [ownRecord ? t("Your Profile") : t("User Profile"), t("Edit")]
            : ownRecord
              ? t("Your Profile")
              : t("User Profile")
        }
        loading={busy}
        footer={
          isEditable ? (
            <>
              <Button variant="primary-outlined" onClick={requestLeave}>
                {t("Cancel")}
              </Button>

              <Button
                variant="primary-filled"
                onClick={async () => {
                  const isValid = await validateAndTouch();
                  if (isValid) {
                    formikAddUser.handleSubmit();
                  }
                }}
              >
                {reactivateUser ? t("Reactivate") : t("Save")}
              </Button>
            </>
          ) : (
            <Button variant="default-soft" fullWidth onClick={requestLeave}>
              {t("Exit")}
            </Button>
          )
        }
      >
        {isEditable && loggedInUser ? (
          <Stack gap="var(--space-7)">
            <UserInformation
              formik={formikAddUser}
              customFieldSettings={customFieldSettings}
              self={self}
            />
          </Stack>
        ) : isLoadingUsers || !formikAddUser.values?.id ? (
          <IdNotFoundPage
            title={"User"}
            isLoading={isLoadingUsers}
            showSkeleton={true}
          />
        ) : (
          // The DS drawer body is a scrolling flex column, so a bare child
          // shrinks instead of scrolling: the summary card collapsed to one
          // row's height. A Stack sizes to its content and scrolls as a whole.
          <Stack gap="var(--space-5)" sx={{ flexShrink: 0 }}>
            <Inline gap="var(--space-4)" align="center" justify="between">
              <Inline gap="var(--space-4)" align="center">
                {/* View mode only: an upload refreshes the profile query, which resets the form. */}
                <ProfilePictureMenu
                  user={{
                    id: formikAddUser.values.id,
                    firstName: formikAddUser.values.firstName,
                    lastName: formikAddUser.values.lastName,
                    profilePicUrl: formikAddUser.values.image,
                  }}
                  ownRecord={ownRecord}
                  isAdminOrHigher={isAdminOrHigher}
                />
                <Stack gap="var(--space-1)">
                  <Typography variant="h5">{userFullName}</Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--color-text-tertiary)" }}
                  >
                    {formikAddUser.values.email}
                  </Typography>
                </Stack>
              </Inline>
              <RenderUserStatus userStatus={formikAddUser.values.status} />
            </Inline>

            <DisplayUserInformation
              ownRecord={ownRecord}
              handleClickEdit={() => setIsEditable(true)}
              formik={formikAddUser}
              handleUpdateUser={handleUpdateUser}
              handleClickReactivateUser={() => {
                setIsEditable(true);
                setReactivateUser(true);
              }}
            />

            <Stack gap="var(--space-2)">
              <Typography variant="h5">{t("Activity")}</Typography>
              <Stack gap="var(--space-4)">
                <Tabs
                  value={activeTab}
                  onChange={(_event, value) => setActiveTab(value)}
                  aria-label={t("Activity")}
                  variant="fullWidth"
                >
                  <Tab value="cards" label={t("Cards")} />
                  <Tab value="transactions" label={t("Transactions")} />
                </Tabs>
                {activeTab === "cards" && (
                  <AssignedCards
                    cards={cards}
                    handleCreateNewCard={handleCreateNewCard}
                    userStatus={formikAddUser.values.status}
                    onCardsChanged={refetchCardData}
                  />
                )}

                {activeTab === "transactions" && (
                  <RecentTransactionList cardHolder={selectedUserId} />
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Drawer>
      {dialog}

      {cardDrawer.record && (
        <CardFormDrawer
          open={cardDrawer.open}
          onClose={handleCloseCreateCardDrawer}
          onAfterClose={cardDrawer.afterClose}
          cardholder={{
            id: formikAddUser.values.id || "",
            firstName: formikAddUser.values.firstName,
            lastName: formikAddUser.values.lastName,
            email: formikAddUser.values.email,
            address: formikAddUser.values.address,
            phone: formikAddUser.values.phone,
            birthdate: formikAddUser.values.birthdate,
            hasPhysicalCard: formikAddUser.values.hasPhysicalCard,
          }}
        />
      )}
    </>
  );
};

export default UserProfileDrawer;
